import { isSameDay } from 'date-fns';
import { RefObject, useCallback, useRef } from 'react';

import { DatePillDropdownAction } from '~/modules/date-pill-dropdown/DatePillDropdown';
import { formatPillDate } from '~/views/news/utils/utils';

export type HandleSelectDateArg = string | { dateFrom: string; dateTo: string };

interface UseListScrollProps<T> {
  listRef: RefObject<HTMLDivElement | null>;
  items: T[];
  getItemDate: (item: T) => Date;
  handleSelectSpecificDate: (arg: HandleSelectDateArg) => void;
}

export const useListScroll = <T>({
  listRef,
  items,
  getItemDate,
  handleSelectSpecificDate,
}: UseListScrollProps<T>) => {
  const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const registerGroupRef = useCallback(
    (key: string) => (element: HTMLDivElement | null) => {
      if (element) {
        groupRefs.current.set(key, element);
      } else {
        groupRefs.current.delete(key);
      }
    },
    []
  );

  const scrollToPosition = (top = 0, behavior: ScrollBehavior = 'smooth') => {
    const element = listRef.current;

    if (!element) {
      return;
    }

    element.scrollTo({
      top: top - element.offsetTop - 10,
      behavior,
    });
  };

  const scrollToDate = useCallback((isoDate: string) => {
    const key = formatPillDate(new Date(isoDate));
    const groupEl = groupRefs.current.get(key);

    if (groupEl) {
      groupEl.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleRangeSelection = (action: DatePillDropdownAction) => {
    if (action.dateFrom && action.dateTo) {
      handleSelectSpecificDate({
        dateFrom: action.dateFrom,
        dateTo: action.dateTo,
      });
    }
  };

  const handleSingleDateSelection = (action: DatePillDropdownAction) => {
    const date = action.date;

    if (date) {
      const actionDate = new Date(date);
      const formattedPillDate = formatPillDate(actionDate);
      const hasBeenFetched = items.find(item =>
        isSameDay(actionDate, getItemDate(item))
      );
      const pillDateDom = groupRefs.current.get(formattedPillDate);

      if (hasBeenFetched && pillDateDom) {
        scrollToPosition(pillDateDom.offsetTop);
      } else {
        handleSelectSpecificDate(date);
      }
    }
  };

  const handleDatePillChange = (action: DatePillDropdownAction) => {
    if (action.dateFrom && action.dateTo) {
      handleRangeSelection(action);
    } else if (action.date) {
      handleSingleDateSelection(action);
    }
  };

  return {
    scrollToPosition,
    scrollToDate,
    handleDatePillChange,
    registerGroupRef,
  };
};
